import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type PartyRef = components['schemas']['PartyRefResponse'];
export type Person = components['schemas']['PersonResponse'];
export type NewPerson = components['schemas']['CreatePersonRequest'];
export type PersonEdits = components['schemas']['UpdatePersonRequest'];
export type LegalEntity = components['schemas']['LegalEntityResponse'];
export type NewLegalEntity = components['schemas']['CreateLegalEntityRequest'];
export type LegalEntityEdits = components['schemas']['UpdateLegalEntityRequest'];
export type RelationshipRole = components['schemas']['PartyRelationshipRoleResponse'];
export type NewPropertyParty = components['schemas']['AddPropertyPartyRequest'];
export type PropertyPartyEdits = components['schemas']['UpdatePropertyPartyRequest'];

/**
 * The party model's own hooks (M1-25): search for the pickers that add a
 * property party or link a relationship, Person/LegalEntity CRUD, the
 * per-account relationship-role catalog, and property-party add/edit/remove.
 */

/** Typeahead search — disabled below a 2-character query, the same floor AddressEndpoints uses. */
export function useSearchParties(q: string, kind?: 'Person' | 'LegalEntity') {
  const trimmed = q.trim();
  return useQuery({
    queryKey: ['parties', 'search', trimmed, kind],
    enabled: trimmed.length >= 2,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/parties', {
        params: { query: { q: trimmed, kind } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/parties failed: ${response.status}`);
      }
      return data;
    },
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (person: NewPerson) => {
      const { data, response } = await apiClient.POST('/api/v1/parties/persons', { body: person });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/parties/persons failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] }),
  });
}

export function useUpdatePerson(partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edits: PersonEdits) => {
      const { data, response } = await apiClient.PUT('/api/v1/parties/persons/{id}', {
        params: { path: { id: partyId } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/parties/persons/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] }),
  });
}

export function useLegalEntity(partyId: string | undefined) {
  return useQuery({
    queryKey: ['parties', 'legal-entities', partyId],
    enabled: partyId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/parties/legal-entities/{id}', {
        params: { path: { id: partyId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/parties/legal-entities/{id} failed: ${response.status}`);
      }
      return data;
    },
  });
}

export function useCreateLegalEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entity: NewLegalEntity) => {
      const { data, response } = await apiClient.POST('/api/v1/parties/legal-entities', { body: entity });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/parties/legal-entities failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] }),
  });
}

export function useUpdateLegalEntity(partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edits: LegalEntityEdits) => {
      const { data, response } = await apiClient.PUT('/api/v1/parties/legal-entities/{id}', {
        params: { path: { id: partyId } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/parties/legal-entities/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parties', 'legal-entities', partyId] });
      void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] });
    },
  });
}

/** Link a Person to a LegalEntity with a role — "from the entity page" (the AC's own words). */
export function useLinkPartyRelationship(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (link: { personPartyId: string; roleId: string; notes: string | null }) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/parties/legal-entities/{entityId}/relationships',
        { params: { path: { entityId } }, body: link },
      );
      if (response.status === 400) {
        throw new DuplicateRelationshipError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`POST .../relationships failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parties', 'legal-entities', entityId] });
      void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] });
    },
  });
}

export class DuplicateRelationshipError extends Error {
  constructor(public readonly problem: unknown) {
    super('This person already has this role on this entity.');
    this.name = 'DuplicateRelationshipError';
  }
}

export function useUnlinkPartyRelationship(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (relationshipId: string) => {
      const { response } = await apiClient.DELETE('/api/v1/parties/relationships/{id}', {
        params: { path: { id: relationshipId } },
      });
      if (!response.ok) {
        throw new Error(`DELETE /api/v1/parties/relationships/{id} failed: ${response.status}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parties', 'legal-entities', entityId] });
      void queryClient.invalidateQueries({ queryKey: ['parties', 'search'] });
    },
  });
}

/** Active roles only by default — every picker's use case; Settings passes includeInactive itself. */
export function usePartyRelationshipRoles(includeInactive = false) {
  return useQuery({
    queryKey: ['party-relationship-roles', { includeInactive }],
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/party-relationship-roles', {
        params: { query: { includeInactive } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/party-relationship-roles failed: ${response.status}`);
      }
      return data;
    },
  });
}

function useMutateRelationshipRoles() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['party-relationship-roles'] });
}

export function useCreatePartyRelationshipRole() {
  const invalidate = useMutateRelationshipRoles();
  return useMutation({
    mutationFn: async (label: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/party-relationship-roles', {
        body: { label },
      });
      if (response.status === 400) {
        throw new DuplicateRoleLabelError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/party-relationship-roles failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: invalidate,
  });
}

export class DuplicateRoleLabelError extends Error {
  constructor(public readonly problem: unknown) {
    super('Another active role already has this label.');
    this.name = 'DuplicateRoleLabelError';
  }
}

export function useUpdatePartyRelationshipRole(id: string) {
  const invalidate = useMutateRelationshipRoles();
  return useMutation({
    mutationFn: async (edits: { label: string; sortOrder: number }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/party-relationship-roles/{id}', {
        params: { path: { id } },
        body: edits,
      });
      if (response.status === 400) {
        throw new DuplicateRoleLabelError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/party-relationship-roles/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useSetPartyRelationshipRoleActive(id: string) {
  const invalidate = useMutateRelationshipRoles();
  return useMutation({
    mutationFn: async (active: boolean) => {
      const { data, response } = await apiClient.POST(
        active
          ? '/api/v1/party-relationship-roles/{id}/activate'
          : '/api/v1/party-relationship-roles/{id}/deactivate',
        { params: { path: { id } } },
      );
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/party-relationship-roles/{id}/(de)activate failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: invalidate,
  });
}

// --- Property parties (the Parties section on PropertyDetail) --------------

/**
 * After any change to a property's links: the property, and every job query
 * (M2-21) — a job's appointment emails go to the property's PRIMARY
 * occupant/tenant, and each job page names that recipient and whether "on my
 * way" can reach anyone. Adding, removing or re-primarying a link moves both.
 */
function invalidateLinks(queryClient: ReturnType<typeof useQueryClient>, propertyId: string) {
  void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
  void queryClient.invalidateQueries({ queryKey: ['jobs'] });
}

export function useAddPropertyParty(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (link: NewPropertyParty) => {
      const { data, error, response } = await apiClient.POST('/api/v1/properties/{propertyId}/parties', {
        params: { path: { propertyId } },
        body: link,
      });
      if (response.status === 400) {
        throw new Error(
          Object.values((error as { errors?: Record<string, string[]> } | undefined)?.errors ?? {})
            .flat()
            .join(' ') || 'That party already has this role on this property.',
        );
      }
      if (!response.ok || !data) {
        throw new Error(`POST .../parties failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateLinks(queryClient, propertyId),
  });
}

export function useUpdatePropertyParty(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, edits }: { id: string; edits: PropertyPartyEdits }) => {
      const { data, response } = await apiClient.PUT('/api/v1/properties/{propertyId}/parties/{id}', {
        params: { path: { propertyId, id } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT .../parties/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateLinks(queryClient, propertyId),
  });
}

export function useRemovePropertyParty(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await apiClient.DELETE('/api/v1/properties/{propertyId}/parties/{id}', {
        params: { path: { propertyId, id } },
      });
      if (!response.ok) {
        throw new Error(`DELETE .../parties/{id} failed: ${response.status}`);
      }
    },
    onSuccess: () => invalidateLinks(queryClient, propertyId),
  });
}
